import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

import * as authActions from '../../Redux/Auth/AuthActions.js';
import MediaComponent from '../../Utils/Media/media-component.js';
import ContactForm from '../../Utils/Forms/ContactForm/contact-form-component.js';
import { getContactData } from '../../Public/contact-data.js';

const EASE = [0.22, 1, 0.36, 1];

/**
 * "CONTACT US" section (light theme).
 * Left: headline + intro, then the tower-crane image shown cleanly in a
 * rounded frame. Right: the message form on a light card.
 *
 * `showContactInfo` marks the dedicated /contact page (renders the H1);
 * embedded as a CTA elsewhere it stays an H2. No highlight pills, collage,
 * or social row, just the crane image and the form.
 */
function Contact({ showContactInfo = false }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { dir = 'rtl', language = 'he' } = useSelector((state) => state.systemData || {});
    const txt = useMemo(() => getContactData(language), [language]);

    useEffect(() => {
        dispatch(authActions.isLoggedIn());
    }, [dispatch]);

    const { hero, image } = txt;
    const HeroTag = showContactInfo ? motion.h1 : motion.h2;

    return (
        <section
            id="contact"
            dir={dir}
            className="w-full scroll-mt-24 bg-white pt-16 md:pt-24 pb-20 md:pb-28 px-5 md:px-8 overflow-hidden"
        >
            {/* Legacy anchor, some CTAs (image grid, quick-call) target #contact-form */}
            <span id="contact-form" aria-hidden="true" />
            <div className="max-w-[1380px] mx-auto grid grid-cols-1 gap-y-10 lg:grid-cols-[1fr_1.15fr] lg:gap-x-16 lg:gap-y-0 items-start">

                {/* ── Headline + intro ── (mobile: 1st, desktop: top-left) */}
                <div className="order-1 lg:col-start-1 lg:row-start-1 text-center sm:text-start">
                    <HeroTag
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, ease: EASE }}
                        className="font-secondary font-bold text-5xl md:text-6xl leading-[0.85] text-neutral-900"
                    >
                        <span className="block">{hero.titleTop}</span>
                        <span className="block text-secondary-500">{hero.titleBottom}</span>
                    </HeroTag>

                    <p className="mt-6 max-w-md mx-auto sm:mx-0 text-lg md:text-xl text-neutral-500 leading-relaxed">
                        {hero.intro}
                    </p>
                </div>

                {/* ── Form card ── (mobile: 2nd, desktop: right, spans both rows) */}
                <div className="order-2 lg:col-start-2 lg:row-start-1 lg:row-span-2">
                    <div className="bg-neutral-50 rounded-[36px] border border-neutral-100 px-7 md:px-14 py-10 md:py-14 shadow-[0_10px_45px_rgba(0,0,0,0.05)]">
                        <ContactForm onSuccess={() => navigate('/thank-you')} />
                    </div>
                </div>

                {/* ── Crane ── (mobile: 3rd, rises out of the form to the bottom edge) */}
                {image && (
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-60px' }}
                        transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
                        className="order-3 lg:col-start-1 lg:row-start-2  lg:mt-10 self-end mb-12"
                    >
                        <MediaComponent
                            src={image}
                            alt=""
                            width={700}
                            quality={70}
                            useSekeleton={false}
                            objectFit="contain"
                            wrapperClassName="w-full"
                            className="object-bottom"
                        />
                    </motion.div>
                )}

            </div>
        </section>
    );
}

export default Contact;
